import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { theme } from '../../theme/theme.js';
import OrderProductApi from '../../api/OrderProductApi';
import OrderLineItemApi from '../../api/OrderLineItemApi';

const WIZARD_STEPS = {
  SELECT: 1,
  EDIT: 2,
};

const generateKey = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const mapInitialItems = (items = [], fallbackCurrency = 'USD') =>
  items.map(item => {
    const rawDiscount =
      item.discount !== undefined ? item.discount : item.discountPercent;
    const normalizedDiscount =
      rawDiscount === null || rawDiscount === undefined || rawDiscount === ''
        ? ''
        : Number(rawDiscount);

    return {
      uid: item.id || generateKey(),
      id: item.id,
      productId: item.productid || item.productId,
      productPriceId: item.productpriceid || item.productPriceId,
      productName: item.product_name || item.productName || item.name,
      productCode: item.product_code || item.productCode || '—',
      productCategory: item.product_category || item.productCategory || '—',
      currency: item.currency || fallbackCurrency,
      quantity: Number(item.quantity || 1),
      listPrice: Number(
        item.list_price !== undefined ? item.list_price : item.listPrice || 0
      ),
      salesPrice: Number(
        item.sales_price !== undefined ? item.sales_price : item.salesPrice || 0
      ),
      discountPercent:
        normalizedDiscount === '' || Number.isNaN(normalizedDiscount)
          ? ''
          : normalizedDiscount,
    };
  });

const OrderProductWizard = ({
  show,
  onHide,
  orderId,
  orderCurrency,
  onSaveSuccess,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [apiError, setApiError] = useState(null);
  const [currentStep, setCurrentStep] = useState(WIZARD_STEPS.SELECT);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [deletedIds, setDeletedIds] = useState([]);
  const [initialItemsLoading, setInitialItemsLoading] = useState(false);

  const selectedProductsList = useMemo(() => selectedProducts, [selectedProducts]);
  const newProductsCount = useMemo(
    () => selectedProductsList.filter(item => !item.id).length,
    [selectedProductsList]
  );
  const selectedCountByPriceId = useMemo(() => {
    const map = {};
    selectedProductsList.forEach(item => {
      const key = item.productPriceId || item.productId;
      if (!key) {
        return;
      }
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [selectedProductsList]);

  const hydrateSelectedProducts = useCallback(
    items => {
      if (Array.isArray(items) && items.length > 0) {
        setSelectedProducts(mapInitialItems(items, orderCurrency));
        setCurrentStep(WIZARD_STEPS.EDIT);
      } else {
        setSelectedProducts([]);
        setCurrentStep(WIZARD_STEPS.SELECT);
      }
      setDeletedIds([]);
      setSaveError(null);
    },
    [orderCurrency]
  );

  const fetchExistingOrderProducts = useCallback(async () => {
    if (!orderId) {
      hydrateSelectedProducts([]);
      return;
    }
    setInitialItemsLoading(true);
    try {
      const response = await OrderLineItemApi.fetchByOrder(orderId);
      if (response?.success) {
        hydrateSelectedProducts(response.data || []);
      } else {
        hydrateSelectedProducts([]);
      }
    } catch (error) {
      console.error('Failed to load order products:', error);
      hydrateSelectedProducts([]);
    } finally {
      setInitialItemsLoading(false);
    }
  }, [hydrateSelectedProducts, orderId]);

  useEffect(() => {
    if (show) {
      fetchExistingOrderProducts();
    } else {
      setSelectedProducts([]);
      setCurrentStep(WIZARD_STEPS.SELECT);
      setSearchTerm('');
      setSearchResults([]);
      setApiError(null);
      setDeletedIds([]);
      setSaveError(null);
    }
  }, [show, fetchExistingOrderProducts]);

  const performSearch = async (term = searchTerm) => {
    if (!orderCurrency) {
      setApiError('Order currency is missing. Please update the record and try again.');
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setApiError(null);

    try {
      const response = await OrderProductApi.searchProducts({
        currency: orderCurrency,
        search: term,
      });

      if (!response?.success) {
        throw new Error(response?.message || 'Failed to fetch products');
      }

      setSearchResults(response?.data || []);
    } catch (error) {
      console.error('Failed to search products:', error);
      setApiError(error?.message || 'Failed to fetch products');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (show && orderCurrency) {
      performSearch('');
    }
  }, [show, orderCurrency]);

  const handleAddProduct = product => {
    const listPrice =
      product.listPrice !== null && product.listPrice !== undefined
        ? Number(product.listPrice)
        : 0;

    setSelectedProducts(prev => [
      ...prev,
      {
        uid: generateKey(),
        productId: product.productId,
        productPriceId: product.productPriceId,
        productName: product.productName,
        productCode: product.productCode,
        productCategory: product.productCategory,
        currency: product.currency || orderCurrency || 'USD',
        quantity: 1,
        listPrice,
        salesPrice: listPrice,
        discountPercent: '',
      },
    ]);
  };

  const handleRemoveProduct = uid => {
    setSelectedProducts(prev => {
      const itemToRemove = prev.find(item => item.uid === uid);
      if (itemToRemove?.id) {
        setDeletedIds(prevDeleted => [...prevDeleted, itemToRemove.id]);
      }
      return prev.filter(item => item.uid !== uid);
    });
  };

  const handleQuantityChange = (uid, quantity) => {
    setSelectedProducts(prev =>
      prev.map(item =>
        item.uid === uid
          ? {
              ...item,
              quantity: quantity < 1 ? 1 : quantity,
            }
          : item
      )
    );
  };

  const handleSalesPriceChange = (uid, price) => {
    setSelectedProducts(prev =>
      prev.map(item => {
        if (item.uid !== uid) {
          return item;
        }
        const normalizedPrice = price < 0 ? 0 : price;
        return {
          ...item,
          salesPrice: normalizedPrice,
        };
      })
    );
  };

  const handleDiscountChange = (uid, discount) => {
    setSelectedProducts(prev =>
      prev.map(item => {
        if (item.uid !== uid) {
          return item;
        }
        if (discount === '' || discount === null) {
          return { ...item, discountPercent: '' };
        }
        let normalized = Number(discount);
        if (Number.isNaN(normalized)) {
          normalized = '';
        } else {
          if (normalized < 0) normalized = 0;
          if (normalized > 100) normalized = 100;
        }
        return { ...item, discountPercent: normalized };
      })
    );
  };

  const handleSearchSubmit = event => {
    event.preventDefault();
    performSearch();
  };

  const handleModalClose = () => {
    setSaveError(null);
    setIsSaving(false);
    onHide();
  };

  const handleNext = () => {
    if (selectedProductsList.length > 0) {
      setCurrentStep(WIZARD_STEPS.EDIT);
    }
  };

  const handleBack = () => {
    setCurrentStep(WIZARD_STEPS.SELECT);
  };

  const handleSave = async () => {
    if (!orderId) {
      setSaveError('Order ID is required to save products.');
      return;
    }

    const payload = selectedProductsList.map(item => {
      const quantity = Number(item.quantity) > 0 ? Number(item.quantity) : 1;
      const listPrice =
        item.listPrice !== null && item.listPrice !== undefined
          ? Number(item.listPrice)
          : 0;
      const salesPrice =
        item.salesPrice !== null && item.salesPrice !== undefined
          ? Number(item.salesPrice)
          : listPrice;
      const effectiveDiscount =
        item.discountPercent === '' || item.discountPercent === null || item.discountPercent === undefined
          ? 0
          : Number(item.discountPercent);
      const discountMultiplier =
        Number.isNaN(effectiveDiscount) || effectiveDiscount <= 0
          ? 0
          : Math.min(100, effectiveDiscount) / 100;
      const netSalesPrice = salesPrice * (1 - discountMultiplier);
      const totalPrice = quantity * netSalesPrice;

      return {
        id: item.id,
        productId: item.productId,
        productPriceId: item.productPriceId,
        quantity,
        listPrice,
        salesPrice,
        discountPercent:
          item.discountPercent === '' || item.discountPercent === null || item.discountPercent === undefined
            ? 0
            : Number(item.discountPercent),
        totalPrice,
        description: null,
        name: item.productName || 'Order Line Item',
      };
    });

    setIsSaving(true);
    setSaveError(null);

    try {
      const response = await OrderProductApi.createOrderProducts({
        orderId,
        items: payload,
        deletedIds,
      });

      if (!response?.success) {
        throw new Error(response?.message || 'Failed to save products');
      }

      if (onSaveSuccess) {
        onSaveSuccess(response.data);
      }

      handleModalClose();
    } catch (error) {
      console.error('Failed to save order products:', error);
      setSaveError(error?.message || 'Failed to save products');
    } finally {
      setIsSaving(false);
    }
  };

  const renderSearchResults = () => {
    if (apiError) {
      return (
        <div className='text-center py-4 text-danger'>
          {apiError}
        </div>
      );
    }

    if (isSearching) {
      return (
        <div className='text-center py-4'>
          <Spinner animation='border' role='status' size='sm' className='me-2' />
          Searching products...
        </div>
      );
    }

    if (searchResults.length === 0) {
      return (
        <div className='text-center py-4 text-muted'>
          No products match the current search.
        </div>
      );
    }

    return (
      <div className='table-responsive product-results-table'>
        <table className='table table-hover align-middle mb-0'>
          <thead>
            <tr>
              <th>Product Name</th>
              <th>Code</th>
              <th>Category</th>
              <th className='text-end'>List Price</th>
              <th className='text-center'>Action</th>
            </tr>
          </thead>
          <tbody>
            {searchResults.map(product => {
              const key = product.productPriceId || product.productId;
              const alreadySelectedCount = selectedCountByPriceId[key] || 0;
              return (
                <tr key={`${product.productPriceId || product.productId}`}>
                  <td>
                    {product.productName}
                    {alreadySelectedCount > 0 && (
                      <span className='badge bg-light text-muted ms-2'>
                        Added {alreadySelectedCount}
                      </span>
                    )}
                  </td>
                  <td>{product.productCode || '—'}</td>
                  <td>{product.productCategory || '—'}</td>
                  <td className='text-end'>
                    {Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: product.currency || 'USD',
                    }).format(Number(product.listPrice) || 0)}
                  </td>
                  <td className='text-center'>
                    <Button
                      size='sm'
                      variant='outline-primary'
                      onClick={() => handleAddProduct(product)}
                    >
                      {alreadySelectedCount > 0 ? 'Add Again' : 'Add'}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSelectedProducts = (showPersisted = true) => {
    const displayList = showPersisted
      ? selectedProductsList
      : selectedProductsList.filter(item => !item.id);
    const grandTotal = displayList.reduce((sum, item) => {
      const quantity = Number(item.quantity) || 1;
      const salesPrice = Number(item.salesPrice) || 0;
      const discount =
        item.discountPercent === '' || item.discountPercent === null || item.discountPercent === undefined
          ? 0
          : Math.min(100, Math.max(0, Number(item.discountPercent)));
      const discountMultiplier = discount / 100;
      const netPrice = salesPrice * (1 - discountMultiplier);
      return sum + quantity * netPrice;
    }, 0);

    if (displayList.length === 0) {
      return (
        <div className='text-center py-4 text-muted'>
          No products selected yet. Add products from the list above.
        </div>
      );
    }

    return (
      <div className='table-responsive selected-products-table'>
        <table className='table table-bordered align-middle mb-0'>
          <thead>
            <tr>
              <th>Product</th>
              <th>Code</th>
              <th>Category</th>
              <th style={{ width: '120px' }}>Quantity</th>
              <th className='text-end'>List Price</th>
              <th className='text-end'>Sales Price</th>
              <th style={{ width: '140px' }}>Discount %</th>
              <th className='text-end'>Line Total</th>
              <th className='text-center'>Remove</th>
            </tr>
          </thead>
          <tbody>
            {displayList.map(item => (
              <tr key={item.uid}>
                <td>{item.productName}</td>
                <td>{item.productCode || '—'}</td>
                <td>{item.productCategory || '—'}</td>
                <td>
                  <Form.Control
                    type='number'
                    min={1}
                    value={item.quantity}
                    onChange={e => handleQuantityChange(item.uid, Number(e.target.value))}
                  />
                </td>
                <td className='text-end'>
                  {Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: item.currency || 'USD',
                  }).format(Number(item.listPrice) || 0)}
                </td>
                <td>
                  <Form.Control
                    type='number'
                    min={0}
                    value={item.salesPrice}
                    onChange={e => handleSalesPriceChange(item.uid, Number(e.target.value))}
                    className='text-end'
                  />
                </td>
                <td>
                  <Form.Control
                    type='number'
                    min={0}
                    max={100}
                    value={item.discountPercent}
                    placeholder='—'
                    onChange={e => handleDiscountChange(item.uid, e.target.value === '' ? '' : Number(e.target.value))}
                    className='text-end'
                  />
                </td>
                <td className='text-end'>
                  {(() => {
                    const quantity = Number(item.quantity) || 1;
                    const salesPrice = Number(item.salesPrice) || 0;
                    const discount =
                      item.discountPercent === '' || item.discountPercent === null || item.discountPercent === undefined
                        ? 0
                        : Math.min(100, Math.max(0, Number(item.discountPercent)));
                    const netPrice = salesPrice * (1 - discount / 100);
                    return Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: item.currency || 'USD',
                    }).format(quantity * netPrice);
                  })()}
                </td>
                <td className='text-center'>
                  <Button
                    size='sm'
                    variant='outline-danger'
                    onClick={() => handleRemoveProduct(item.uid)}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className='text-end mt-3 fw-semibold'>
          Grand Total:{' '}
          {Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: displayList[0]?.currency || 'USD',
          }).format(grandTotal)}
        </div>
      </div>
    );
  };

  const renderStepIndicator = () => (
    <div className='mb-3 small text-muted'>
      <span style={{ fontWeight: currentStep === WIZARD_STEPS.SELECT ? 600 : 400 }}>
        1. Select Products
      </span>
      <span> &nbsp;→&nbsp; </span>
      <span style={{ fontWeight: currentStep === WIZARD_STEPS.EDIT ? 600 : 400 }}>
        2. Edit Products
      </span>
    </div>
  );

  return (
    <Modal
      show={show}
      onHide={handleModalClose}
      size='xl'
      backdrop='static'
      centered
      dialogClassName='order-product-wizard-modal'
    >
      <Modal.Header closeButton>
        <div>
          <Modal.Title>Add Products to Order</Modal.Title>
          <div className='text-muted small'>
            Order ID: {orderId || '—'} • Currency: {orderCurrency || '—'}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        {initialItemsLoading ? (
          <div className='text-center py-5'>
            <Spinner animation='border' role='status' className='me-2' />
            Loading order products...
          </div>
        ) : (
          <>
            {renderStepIndicator()}
            {currentStep === WIZARD_STEPS.SELECT ? (
              <>
                <section className='product-search-section mb-4'>
                  <form onSubmit={handleSearchSubmit} className='d-flex align-items-center gap-3'>
                    <Form.Control
                      type='text'
                      placeholder='Search by name, code, or category'
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      disabled={!orderCurrency}
                    />
                    <Button type='submit' variant='primary' disabled={isSearching || !orderCurrency}>
                      {isSearching ? 'Searching...' : 'Search'}
                    </Button>
                  </form>
                  <div className='mt-2 text-muted small'>
                    {orderCurrency
                      ? `${newProductsCount} new product${newProductsCount === 1 ? '' : 's'} selected.`
                      : 'Cannot search without a valid order currency.'}
                  </div>
                </section>

                <section className='product-results-section mb-4'>
                  <h6
                    style={{
                      fontFamily: theme.typography.fontFamily.primary,
                      fontWeight: theme.typography.fontWeight.semibold,
                    }}
                  >
                    Search Results
                  </h6>
                  {renderSearchResults()}
                </section>

                <section className='selected-products-section'>
                  <h6
                    style={{
                      fontFamily: theme.typography.fontFamily.primary,
                      fontWeight: theme.typography.fontWeight.semibold,
                    }}
                  >
                    Selected Products ({newProductsCount})
                  </h6>
                  {renderSelectedProducts(false)}
                </section>
              </>
            ) : (
              <section className='edit-products-section'>
                <h6
                  style={{
                    fontFamily: theme.typography.fontFamily.primary,
                    fontWeight: theme.typography.fontWeight.semibold,
                  }}
                >
                  Edit Selected Products
                </h6>
                {renderSelectedProducts(true)}
              </section>
            )}
            {saveError && (
              <div className='alert alert-danger mt-3 mb-0'>
                {saveError}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className='d-flex justify-content-between'>
        <div className='text-muted small'>
          {currentStep === WIZARD_STEPS.SELECT
            ? 'Select products and click Next to review and edit them.'
            : 'Adjust quantities, prices, or discounts before saving.'}
        </div>
        <div>
          <Button variant='outline-secondary' className='me-2' onClick={handleModalClose}>
            Cancel
          </Button>
          {currentStep === WIZARD_STEPS.SELECT ? (
            <Button
              variant='primary'
              disabled={initialItemsLoading || selectedProductsList.length === 0}
              onClick={handleNext}
            >
              Next
            </Button>
          ) : (
            <>
              <Button
                variant='outline-primary'
                className='me-2'
                onClick={handleBack}
                disabled={initialItemsLoading}
              >
                Back to Add Products
              </Button>
              <Button
                variant='primary'
                disabled={
                  initialItemsLoading ||
                  (selectedProductsList.length === 0 && deletedIds.length === 0) ||
                  isSaving
                }
                onClick={handleSave}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default OrderProductWizard;
